import { graphql, useStaticQuery } from "gatsby";
import styled from "styled-components";
import React, { useState } from "react";
import { P, H4, Small, IconHeartFilled, IconHighFiveFilled, IconRibbonFilled } from "@upsolve/ui";
import withViewportWatch from "../analytics/withViewportWatch";
import trackComponentImpression from "../analytics/trackComponentImpression";
import trackComponentAction from "../analytics/trackComponentAction";
import getMyUpsolveEntryUrl from "../Links/getMyUpsolveEntryUrl";
import ImageInContext from "../Media/ImageInContext";
import { MyUpsolveLink, MyUpsolveButtonLink } from "../Links/MyUpsolveLink";
import upsolveStats from "../../../content/onBuild/upsolveStats.json";
import UpsolveLogo from "../../../static/images/upsolve-logo.svg";
import _ from "lodash";

const trackingProps = {
  componentName: "CTAUpsolveCommunity",
  componentVersion: 1,
};

const REACTION_TYPE_ICON_MAP = {
  goodQuestion: IconRibbonFilled,
  heart: IconHeartFilled,
  cheer: IconHighFiveFilled,
};

/**
 * CTAUpsolveCommunity
 * - v0: Initial implementation w/ 3 posts
 * - v1: Design adjustments. Lean into live Q&A and conversations. Make posts more clickable
 *
 * @component
 * @version 1
 */
const CTAUpsolveCommunity = (props) => {
  // Impression Tracking
  const [impressionTracked, setImpressionTracked] = useState(false);
  if (props.isInViewport && !impressionTracked) {
    setImpressionTracked(true);
    props.stopWatcher();
    trackComponentImpression({
      ...trackingProps,
      pageContext: props.trackingPageContext,
    });
  }

  const searchResult = props.postSearchResults || {};
  if (!searchResult.query) return null;

  return (
    <CTAUpsolveCommunityWrapper dataTestId={props.dataTestId}>
      <CommunityTile
        qaVariant={props.qaVariant}
        searchResult={searchResult}
        trackingPageContext={props.trackingPageContext}
      />
    </CTAUpsolveCommunityWrapper>
  );
};

const CommunityTile = (props) => {
  const { pictures } = useStaticQuery(graphql`
    query CTAUpsolveCommunityPictures {
      pictures: allContentfulReview(
        filter: { image: { id: { ne: null } } }
        sort: { fields: [date], order: ASC }
        limit: 10
      ) {
        nodes {
          image {
            altText
            image {
              fluid(maxWidth: 100) {
                ...GatsbyContentfulFluid_withWebp
              }
            }
          }
        }
      }
    }
  `);

  const searchQuery = props.searchResult.query.trim();
  const searchResults = props.searchResult.results || [];

  return (
    <StyledCommunityTile>
      <MyUpsolveLink
        className="upsolve-community__group"
        href={getMyUpsolveEntryUrl({
          triggeredByActionId: "CTAUpsolveCommunity.headerDescription.myUpsolveNavigation",
        })}
        onClick={() =>
          trackComponentAction({
            ...trackingProps,
            actionId: "CTAUpsolveCommunity.headerDescription.myUpsolveNavigation",
            actionMethod: "click",
            pageContext: props.trackingPageContext,
          })
        }
      >
        <div className="group__description">
          <H4 color="brand">Live Community Q&A</H4>
          <Small>
            {Number(upsolveStats.active.total || 100).toLocaleString()} Members Online &middot;{" "}
            {Number(upsolveStats.stepActivity.communityPostsCreated || 300).toLocaleString()}+ Posts
          </Small>
          <P color="brand">
            Professional Q&amp;As for "<strong>{_.startCase(searchQuery)}</strong>"
          </P>
        </div>
        <div className="group__picture">
          <UpsolveLogo />
        </div>
      </MyUpsolveLink>
      <div className="upsolve-community__posts">
        {searchResults
          .slice(0, props.qaVariant ? 5 : 3) // only show top 3 results for now
          .map((post, postIndex) => (
            <MyUpsolveLink
              key={postIndex}
              className="posts__post"
              href={`${UPSOLVE_FILER_URL}/community/post/${post.postId}?redirectSignUp=true`}
              onClick={() =>
                trackComponentAction({
                  ...trackingProps,
                  actionId: "CTAUpsolveCommunity.post.myUpsolveNavigation",
                  actionMethod: "click",
                  pageContext: props.trackingPageContext,
                })
              }
            >
              <div className="post__profile">
                <div className="profile__picture">
                  <ImageInContext fluid={pictures.nodes[postIndex % 10].image.image.fluid} />
                </div>
                <div className="profile__description">
                  <P>
                    <strong className="description__name">Upsolve Community Member</strong>
                    {/* TODO: {&nbsp;<sup>{post.post_author_badge_type}</sup>} */}
                    <br />
                    <Small>{post.post_date}</Small>
                  </P>
                </div>
              </div>
              <div className="post__text">
                <P>
                  {props.qaVariant ? (
                    <span>{post.text}</span>
                  ) : (
                    <>
                      <span dangerouslySetInnerHTML={{ __html: post.text }} />
                      <Small>...see more</Small>
                    </>
                  )}
                </P>
              </div>
              <div className="post__reactions">
                <div>
                  {post.reactions_count > 0 && (
                    <div className="icons">
                      {post.reaction_types.map((r) => {
                        const Icon = REACTION_TYPE_ICON_MAP[r];
                        return Icon ? (
                          <div key={r} className={r}>
                            <Icon />
                          </div>
                        ) : null;
                      })}
                      <Small>{post.reactions_count || 0} Reactions</Small>
                    </div>
                  )}
                </div>
                {!props.qaVariant && post.comments_count > 0 && (
                  <Small>
                    <u>View {post.comments_count || 0} Comments</u>
                  </Small>
                )}
              </div>
              {props.qaVariant && post.comments.length > 0 && (
                <div className="post__comments">
                  <P>
                    <strong>Community Replies:</strong>
                  </P>
                  {post.comments.map((comment, commentIndex) => (
                    <div className="post__comment">
                      <div>
                        <div className="profile__picture">
                          <ImageInContext
                            fluid={pictures.nodes[(commentIndex + postIndex + 5) % 10].image.image.fluid}
                          />
                        </div>
                      </div>
                      <P className="post__comment__body">
                        <strong className="post__comment__author">Upsolve Community Member</strong>
                        <br />
                        <Small>{comment}</Small>
                      </P>
                    </div>
                  ))}
                </div>
              )}
            </MyUpsolveLink>
          ))}
        {searchResults.length === 0 ? (
          <>
            <div className="posts__empty">
              <Small>No one has posted about this topic yet.</Small>
            </div>
            <div className="cta-upsolve-community-wrapper__link">
              <MyUpsolveButtonLink
                size="lg"
                href={getMyUpsolveEntryUrl({
                  triggeredByActionId: "CTAUpsolveCommunity.noPostOffer.myUpsolveNavigation",
                })}
                onClick={() =>
                  trackComponentAction({
                    ...trackingProps,
                    actionId: "CTAUpsolveCommunity.noPostOffer.myUpsolveNavigation",
                    actionMethod: "click",
                    pageContext: props.trackingPageContext,
                  })
                }
              >
                Be the first to ask our Community and receive a professional response!
              </MyUpsolveButtonLink>
            </div>
          </>
        ) : (
          <>
            <div className="cta-upsolve-community-wrapper__link">
              <MyUpsolveButtonLink
                size="md"
                width="100%"
                href={getMyUpsolveEntryUrl({
                  triggeredByActionId: "CTAUpsolveCommunity.joinExpertCommunity.myUpsolveNavigation",
                })}
                onClick={() =>
                  trackComponentAction({
                    ...trackingProps,
                    actionId: "CTAUpsolveCommunity.joinExpertCommunity.myUpsolveNavigation",
                    actionMethod: "click",
                    pageContext: props.trackingPageContext,
                  })
                }
              >
                See {Number(upsolveStats.stepActivity.communityPostsCreated).toLocaleString()}+ more conversations
              </MyUpsolveButtonLink>
            </div>
          </>
        )}
      </div>
    </StyledCommunityTile>
  );
};

const StyledCommunityTile = styled.div`
  background: transparent;
  padding: 0;

  .cta-upsolve-community-wrapper__link {
    margin: 0;
    padding: 1em 2em;
    font-weight: 700;
    width: 100%;
    display: flex;
    justify-content: center;
    text-align: center;
    a {
      color: #fff;
    }
  }

  .upsolve-community__group {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75em 1em;
    margin: 0;
    background: ${(props) => props.theme.colors.white[900]};
    border-radius: 24px 24px 0 0;
    border: 1px solid ${(props) => props.theme.colors.white[300]};
    @media (max-width: ${(p) => p.theme.breakpoints[500]}) {
      padding: 0.5em 0.5em;
      flex-direction: column-reverse;
    }
  }

  .upsolve-community__posts {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    padding: 1em 0 0;
    background: ${(props) => props.theme.colors.white[700]};
  }

  .group__picture {
    border-radius: 24px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 120px;
    margin: 20px 0;
    & > div {
      height: 100%;
      width: 100%;
    }
    svg {
      width: 100px;
    }
    img {
      min-width: 150px;
    }
    @media (max-width: ${(p) => p.theme.breakpoints[500]}) {
      margin: 10px 0;
      padding: 0 10px;
    }
  }

  .group__description {
    margin-top: 0.25em;
    margin-left: 0.5em;
    h4 {
      font-weight: 900;
    }
    h4,
    p,
    small {
      margin: 0;
      padding: 0;
      text-align: left;
    }
    small {
      color: ${(props) => props.theme.colors.gray[900]};
      font-weight: 300;
      white-space: nowrap;
    }
  }

  .posts__post {
    flex: 1;
    margin: 0.5em 1.5em;
    padding: 0;
    border-radius: 8px;
    background: white;
    box-shadow: ${(props) => props.theme.effects.shadow[350]};
    border: 2px solid ${(props) => props.theme.colors.white[300]};
    &:hover {
      border: 2px solid ${(props) => props.theme.colors.brand[500]};
    }
    @media (max-width: ${(p) => p.theme.breakpoints[500]}) {
      margin: 0.5em;
    }
  }

  .posts__empty {
    padding: 12px;
    margin: 6px;
    text-align: center;
    small {
      color: ${(props) => props.theme.colors.gray[900]};
    }
    button {
      color: #fff;
    }
  }

  .posts__view_more {
    text-align: center;
    a {
      text-decoration: underline;
    }
  }

  .post__profile {
    padding: 12px 12px 6px;
    margin: 0px;

    display: flex;
    align-items: center;
    border-bottom: 1px solid rgb(240, 241, 242);
  }

  .profile__picture {
    height: 48px;
    width: 48px;
    border-radius: 50%;
    border: 1px solid #ccc;
    overflow: hidden;
    img {
      filter: blur(3px);
    }
  }

  .profile__description {
    p {
      padding: 0 0.5em;
      margin: 0;
    }
    small {
      color: ${(props) => props.theme.colors.gray[900]};
    }
  }

  .post__comments {
    border-top: 2px solid #fbfbfc;
    padding: 0 1.5em;
  }

  .post__comment {
    display: flex;
    align-items: center;
    background: #f0f2f5;
    border-radius: 20px;
    padding: 4px 12px;
    margin-bottom: 0.5em;
  }

  .post__comment__body {
    padding: 0 12px;
  }

  .post__text {
    padding: 0.5em 1.5em 0;
    margin: 0;
    font-size: 12px;
    & > p {
      margin: 0;
      padding: 0;
      text-align: left;
      em {
        font-style: normal;
        font-weight: bold;
      }
      small {
        color: ${(props) => props.theme.colors.brand[500]};
        margin-left: 4px;
      }
    }
  }

  .post__reactions {
    padding: 0.5em 1.25em 1.25em;
    display: flex;
    align-items: center;
    justify-content: space-between;
    & > div {
      display: flex;
      align-items: center;
    }
    .icons {
      display: flex;
      align-items: center;

      div {
        display: flex;
        height: 20px;
        width: 20px;
        border-radius: 10px;
        padding: 3px;
        justify-content: center;
        align-items: center;
        svg {
          height: auto;
          width: auto;
          path {
            fill: white;
          }
        }
        margin-right: -2px;
        &:last-of-type {
          margin-right: 6px;
        }
        &.heart {
          background: ${(props) => props.theme.colors.pink[500]};
          padding: 6px 5px 5px;
        }
        &.goodQuestion {
          background: ${(props) => props.theme.colors.green[300]};
          padding: 5px;
        }
        &.cheer {
          background: ${(props) => props.theme.colors.purple[500]};
        }
      }
    }
    small {
      color: ${(props) => props.theme.colors.brand[500]};
    }
  }
`;

const CTAUpsolveCommunityWrapper = styled.div.attrs((props) => ({
  "data-test-id": props.dataTestId,
}))`
  box-shadow: ${(props) => props.theme.effects.shadow[300]};
  border-radius: 24px;
  overflow: hidden;
  padding: 0;
  & > p {
    padding: 0 1em;
    font-weight: 500;
  }
  @media (max-width: 45em) {
    margin: 0 -1em;
  }
`;

export default withViewportWatch(CTAUpsolveCommunity);
